import { TrendingDown, DollarSign, Building2, Stethoscope } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import type { CarePlanData } from '@/hooks/useCarePlan';

interface SavingsBannerProps {
  plan: CarePlanData;
}

function formatUsd(amount: number | null): string {
  if (amount === null || amount === undefined) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
}

export function SavingsBanner({ plan }: SavingsBannerProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;

  const hasSavings = (plan.savings_vs_national ?? 0) > 0;

  return (
    <div className={`rounded-xl border p-5 ${
      isAhp
        ? hasSavings ? (ahpDark ? 'bg-green-900/20 border-green-700/30' : 'bg-green-50 border-green-200') : (ahpDark ? 'bg-orange-900/20 border-orange-700/30' : 'bg-orange-50 border-orange-200')
        : hasSavings ? 'bg-green-500/10 border-green-500/30' : 'bg-orange-500/10 border-orange-500/30'
    }`}>
      {/* Main savings headline */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg ${
          isAhp
            ? hasSavings ? (ahpDark ? 'bg-green-900/30' : 'bg-green-100') : (ahpDark ? 'bg-orange-900/30' : 'bg-orange-100')
            : hasSavings ? 'bg-green-500/20' : 'bg-orange-500/20'
        }`}>
          <TrendingDown className={`w-5 h-5 ${
            isAhp
              ? hasSavings ? (ahpDark ? 'text-green-400' : 'text-green-700') : (ahpDark ? 'text-orange-400' : 'text-orange-700')
              : hasSavings ? 'text-green-400' : 'text-orange-400'
          }`} />
        </div>
        <div>
          <p className={`text-lg font-bold ${
            isAhp
              ? hasSavings ? (ahpDark ? 'text-green-400' : 'text-green-800') : (ahpDark ? 'text-orange-400' : 'text-orange-800')
              : hasSavings ? 'text-green-400' : 'text-orange-400'
          }`}>
            {hasSavings
              ? `Save ${formatUsd(plan.savings_vs_national)} (${plan.savings_percentage?.toFixed(1)}%)`
              : `${formatUsd(Math.abs(plan.savings_vs_national ?? 0))} above national average`
            }
          </p>
          <p className={`text-sm ${isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground'}`}>
            {hasSavings ? 'vs. national average pricing' : 'nearby hospitals are above average for these procedures'}
          </p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={<DollarSign className="w-4 h-4" />}
          label="Optimized Total"
          value={formatUsd(plan.optimized_total_cost)}
          isAhp={isAhp}
          ahpDark={ahpDark}
        />
        <StatCard
          icon={<DollarSign className="w-4 h-4" />}
          label="National Average"
          value={formatUsd(plan.national_avg_total_cost)}
          isAhp={isAhp}
          ahpDark={ahpDark}
        />
        <StatCard
          icon={<Stethoscope className="w-4 h-4" />}
          label="Procedures"
          value={String(plan.total_procedures)}
          isAhp={isAhp}
          ahpDark={ahpDark}
        />
        <StatCard
          icon={<Building2 className="w-4 h-4" />}
          label="Hospitals Used"
          value={String(plan.total_hospitals_used)}
          isAhp={isAhp}
          ahpDark={ahpDark}
        />
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, isAhp, ahpDark }: {
  icon: React.ReactNode; label: string; value: string; isAhp: boolean; ahpDark: boolean;
}) {
  return (
    <div className={`rounded-lg p-3 ${isAhp ? (ahpDark ? 'bg-white/5' : 'bg-white/60') : 'bg-background/40'}`}>
      <div className={`flex items-center gap-1.5 text-xs mb-1 ${isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-500') : 'text-muted-foreground'}`}>
        {icon}
        {label}
      </div>
      <p className={`text-lg font-bold ${isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'}`}>
        {value}
      </p>
    </div>
  );
}
